import React, { useState } from 'react';
import { ArrowRight, Shield, Users, FileText, CheckCircle, Lightbulb, TrendingUp, Award, Building } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ConsultationModal from '../../components/ConsultationModal';
import DemoModal from '../../components/DemoModal';

const SecurityConsultingPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const consultingServices = [
    {
      icon: Shield,
      title: "Security Strategy Development",
      description: "Develop comprehensive cybersecurity strategies aligned with your business objectives and risk tolerance.",
      features: ["Risk assessment", "Security roadmap", "Budget planning", "Technology selection"]
    },
    {
      icon: Building,
      title: "Security Architecture Design",
      description: "Design robust security architectures that protect your critical assets while enabling business growth.",
      features: ["Zero-trust architecture", "Network segmentation", "Identity management", "Cloud security design"]
    },
    {
      icon: FileText,
      title: "Compliance Consulting",
      description: "Navigate complex regulatory requirements and achieve compliance with industry standards.",
      features: ["GDPR compliance", "ISO 27001 certification", "SOC 2 preparation", "Industry-specific regulations"]
    },
    {
      icon: TrendingUp,
      title: "Security Program Maturity",
      description: "Assess and improve your security program maturity to achieve industry-leading security posture.",
      features: ["Maturity assessment", "Gap analysis", "Improvement roadmap", "KPI development"]
    }
  ];

  const expertise = [
    {
      title: "Strategic Planning",
      description: "Long-term security strategy development and implementation planning",
      icon: Lightbulb
    },
    {
      title: "Risk Management",
      description: "Comprehensive risk assessment and mitigation strategy development",
      icon: Shield
    },
    {
      title: "Compliance",
      description: "Regulatory compliance and industry standard implementation",
      icon: Award
    },
    {
      title: "Team Development",
      description: "Security team building and capability development programs",
      icon: Users
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-slate-50'
    }`}>
      <Header 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
        isDarkMode={isDarkMode}
        toggleDarkMode={() => setIsDarkMode(!isDarkMode)}
      />

      <ConsultationModal 
        isOpen={isConsultationModalOpen} 
        onClose={() => setIsConsultationModalOpen(false)} 
      />
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />

      {/* Hero Section */}
      <section className={`relative py-20 overflow-hidden ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-green-950 text-slate-100' 
          : 'bg-gradient-to-br from-green-50 via-blue-50 to-teal-50 text-slate-900'
      }`}>
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            alt="Security Consulting"
            className="w-full h-full object-cover opacity-10"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-r from-slate-950/90 to-green-950/90' 
              : 'bg-gradient-to-r from-green-50/90 to-teal-50/90'
          }`} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <Users className={`h-16 w-16 mx-auto mb-6 ${
              isDarkMode ? 'text-green-400' : 'text-green-600'
            }`} />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Security <span className={isDarkMode ? "text-green-400" : "text-green-600"}>Consulting</span>
            </h1>
            <p className={`text-xl md:text-2xl mb-8 max-w-3xl mx-auto ${
              isDarkMode ? "text-slate-300" : "text-slate-600"
            }`}>
              Strategic cybersecurity guidance from certified experts to build resilient 
              security programs that protect your business and enable growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsConsultationModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-lg ${
                  isDarkMode 
                    ? "bg-green-600 hover:bg-green-700 text-white hover:shadow-green-500/25" 
                    : "bg-green-600 hover:bg-green-700 text-white hover:shadow-green-500/25"
                }`}
              >
                <Shield className="mr-2 h-5 w-5" />
                Book Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 border-2 ${
                  isDarkMode 
                    ? "border-green-400 text-green-400 hover:bg-green-400 hover:text-slate-900" 
                    : "border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                }`}
              >
                View Case Studies
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Consulting Services Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Expert Consulting Services
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              Comprehensive security consulting to address your unique business challenges
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {consultingServices.map((service, index) => (
              <div key={index} className={`p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow ${
                isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'
              }`}>
                <service.icon className={`h-12 w-12 mb-4 ${
                  isDarkMode ? 'text-green-400' : 'text-green-600'
                }`} />
                <h3 className={`text-xl font-semibold mb-3 ${
                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                }`}>{service.title}</h3>
                <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <CheckCircle className={`h-4 w-4 ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Our Areas of Expertise
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              Deep expertise across all aspects of cybersecurity strategy and implementation
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {expertise.map((area, index) => (
              <div key={index} className={`text-center p-6 rounded-xl ${
                isDarkMode ? 'bg-slate-900' : 'bg-white'
              } shadow-lg hover:shadow-xl transition-shadow`}>
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-green-600' : 'bg-green-100'
                }`}>
                  <area.icon className={`h-8 w-8 ${
                    isDarkMode ? 'text-white' : 'text-green-600'
                  }`} />
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${
                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                }`}>{area.title}</h3>
                <p className={`text-sm ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>{area.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="Security Strategy Meeting"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl" />
            </div>
            
            <div>
              <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${
                isDarkMode ? 'text-slate-100' : 'text-slate-900'
              }`}>
                Transform Your Security Posture
              </h2>
              <p className={`text-lg mb-6 ${
                isDarkMode ? 'text-slate-300' : 'text-slate-600'
              }`}>
                Partner with our certified security experts to develop and implement 
                a comprehensive cybersecurity strategy that protects your business 
                and enables sustainable growth.
              </p>
              <div className="space-y-4">
                {[
                  "Certified security professionals",
                  "Industry-specific expertise",
                  "Proven methodologies",
                  "Ongoing support and guidance"
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className={`h-5 w-5 ${
                      isDarkMode ? 'text-green-400' : 'text-green-600'
                    }`} />
                    <span className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer 
        onConsultationClick={() => setIsConsultationModalOpen(true)}
        onDemoClick={() => setIsDemoModalOpen(true)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default SecurityConsultingPage;